import type {
  PendingAddToCartEvent,
  UseAddToCartButtonProps,
} from '@maestro/commerce-blocks/components/add-to-cart-button/add-to-cart-button.types'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCartContext } from '@maestro/commerce-blocks/context/cart.context'
import { useGlobalContext } from '@maestro/commerce-blocks/context/global.context'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { mapCartEntryToProduct } from '@maestro/commerce-blocks/utils/cart-entry.utils'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import {
  addProductToCartWithQueryId,
  getProductQueryId,
} from '@maestro/core/event/queryid-tracker.utils'
import { type EventPayloadType, EventType } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'
import { useHash } from '@pearlconvert/ui/hooks/use-hash'

export const useAddToCartButton = (props: UseAddToCartButtonProps) => {
  const {
    error,
    hideTitle,
    openCart,
    product,
    notificationType,
    title,
    validateSelection,
    variantId,
    eventProps,
    quantity = 1,
  } = props
  const t = useTranslations('commerce-blocks')

  const {
    addProduct,
    addMultipleProducts,
    cart,
    isLoading,
    resolveCartId,
    getAvailableQuantity,
  } = useCartContext()
  const { addOrUpdateHashParam } = useHash()

  const text = title || t('_globals.add-to-cart')
  const ariaLabelText =
    title || t('_globals.add-to-cart-product', { product: product.name })
  const { publish } = useEventBusContext()
  const { session } = useMaestroSession()
  const isVisibleAddToCart = useMemo(() => {
    return !theme().product.showAddToCartOnlyForLoggedIn || !!session
  }, [session])
  const { searchProductIndex } = useGlobalContext()
  const [pendingAddToCartEvent, setPendingAddToCartEvent] =
    useState<PendingAddToCartEvent | null>(null)

  useEffect(() => {
    if (!pendingAddToCartEvent) {
      return
    }

    if (!pendingAddToCartEvent.waitForResolvedCart) {
      if (isLoading) {
        setPendingAddToCartEvent((current) => {
          if (!current) {
            return null
          }
          return {
            ...current,
            waitForResolvedCart: true,
          }
        })
      }
      return
    }

    if (isLoading) {
      return
    }

    const updatedEntry = cart.entries.find((entry) => {
      return (
        entry.productId === pendingAddToCartEvent.productId &&
        entry.variantId === pendingAddToCartEvent.variantId
      )
    })

    if (
      !updatedEntry ||
      updatedEntry.quantity !== pendingAddToCartEvent.expectedQuantity
    ) {
      return
    }

    const payload: EventPayloadType[EventType.PRODUCT_ADDED_TO_CART] = {
      item: {
        product: mapCartEntryToProduct(updatedEntry),
        quantity: pendingAddToCartEvent.quantity,
        variantId: updatedEntry.variantId,
        atcType: pendingAddToCartEvent.atcType,
        position: pendingAddToCartEvent.productIndex,
      },
      listId: pendingAddToCartEvent.listId,
      listName: pendingAddToCartEvent.listName,
      listType: pendingAddToCartEvent.listType,
      searchServiceIndex: pendingAddToCartEvent.searchServiceIndex,
      authenticatedUserToken: pendingAddToCartEvent.authenticatedUserToken,
      queryID: pendingAddToCartEvent.queryID,
      cart: cart.entries,
      triggeredFrom: pendingAddToCartEvent.triggeredFrom,
      isMember: pendingAddToCartEvent.isMember,
    }

    publish<EventPayloadType[EventType.PRODUCT_ADDED_TO_CART]>(
      EventType.PRODUCT_ADDED_TO_CART,
      payload
    )
    setPendingAddToCartEvent(null)
  }, [cart.entries, isLoading, pendingAddToCartEvent, publish])

  const handleAddToCart = useCallback(async () => {
    if (validateSelection) {
      const isValid = validateSelection()
      if (!isValid) {
        return
      }
    }

    const idToUse = (variantId || product.defaultVariantId) as string
    const existingEntry = cart.entries.find((entry) => {
      return entry.productId === product.id && entry.variantId === idToUse
    })
    const nextQuantity = (existingEntry?.quantity ?? 0) + quantity

    if (!error) {
      if (quantity > 1) {
        addMultipleProducts(
          [{ product, variantId: idToUse, quantity }],
          notificationType
        )
      } else {
        addProduct(product, idToUse, notificationType)
      }

      if (getAvailableQuantity(product, idToUse) >= quantity) {
        const { listId, listName, listType } = eventProps.eventListProps || {}
        const { searchIndex, queryID } = eventProps.eventSearchProps || {}

        const checkedQueryID = queryID || getProductQueryId(product.id) || ''
        const cartId = await resolveCartId()

        if (checkedQueryID) {
          addProductToCartWithQueryId(cartId, product.id, checkedQueryID)
        }

        setPendingAddToCartEvent({
          atcType: hideTitle ? 'quick add' : 'standard',
          authenticatedUserToken: session?.user?.hashedEmail ?? '',
          expectedQuantity: nextQuantity,
          isMember: session?.user?.isMember ?? false,
          listId,
          listName,
          listType,
          productId: product.id,
          quantity,
          queryID: checkedQueryID || undefined,
          searchServiceIndex: searchIndex || searchProductIndex,
          triggeredFrom: eventProps.triggeredFrom,
          variantId: idToUse,
          waitForResolvedCart: false,
          productIndex: eventProps.productIndex,
        })
      }
    }

    if (openCart) {
      addOrUpdateHashParam('cart')
    }
  }, [
    addOrUpdateHashParam,
    addProduct,
    addMultipleProducts,
    cart.entries,
    error,
    eventProps,
    hideTitle,
    notificationType,
    openCart,
    product,
    quantity,
    getAvailableQuantity,
    resolveCartId,
    searchProductIndex,
    session?.user?.hashedEmail,
    session?.user?.isMember,
    validateSelection,
    variantId,
  ])

  return {
    ariaLabelText,
    handleAddToCart,
    isVisibleAddToCart,
    text,
  }
}
